<template>
  <div class="routes">
    <SectionHeader :title="$t('routes.routes')">
      <template v-slot:meta>
        <Link :to="`/company/route_editor`" class="button button-green button-small button-round">{{
          $t('routes.routeEditor.goTo')
        }}</Link>
      </template>
    </SectionHeader>
    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('routes.routeTemplates') }}</h3>
            <div class="header-meta">
              <Link :to="`/company/route_templates/new`" class="button button-green button-round button-small">{{
                $t('routes.addTemplate')
              }}</Link>
            </div>
          </header>
          <div class="content-body" id="external-events">
            <LoadingSpinner v-if="loadingTemplates" />
            <template v-else>
              <div v-if="templates.length === 0" class="alert alert-danger">{{ $t('routes.noTemplates') }}</div>
              <ul v-else class="route-template-wrapper">
                <li
                  v-for="template in templates"
                  :key="template.uuid"
                  :data-uuid="template.uuid"
                  class="route-template fc-event fc-h-event fc-daygrid-event fc-daygrid-block-event"
                >
                  <div>
                    <h3>{{ template.name }}</h3>
                    <p>
                      {{ $t('routes.type') }}:
                      {{ templateTypeTitle(template.template_type) }}
                    </p>
                  </div>
                  <Link :to="`/company/route_templates/${template.uuid}`" class="material-icons icon-edit">edit</Link>
                </li>
              </ul>
            </template>
          </div>
        </div>
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>
              {{ $t('routes.routeCalendar') }}
              <LoadingSpinner
                narrow
                style="float: right; margin-left: 10px"
                v-show="!routeEventsRepository || loadingRouteEvents"
              />
            </h3>
            <div class="header-meta"></div>
          </header>
          <div class="content-body">
            <FullCalendar ref="fullCalendar" :options="calendarOptions" v-if="routeEventsRepository" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Gotta import FullClandar first
// eslint-disable-next-line prettier/prettier
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
// somehow the linter needs this too
// eslint-disable-next-line prettier/prettier
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'

import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'
import stringUtils from '@/components/mixins/stringUtils'

import routeTemplatesApi from '@/api/routeTemplates'
import routeEventsApi from '@/api/route_events'
import vehiclesApi from '@/api/vehicles'
import RouteEventsRepository from '@/utils/RouteEventsRepository'

export default {
  name: 'CompaniesRoutesIndex',
  inject: ['companyUser'],
  components: {
    CompanyNav,
    FullCalendar,
    LoadingSpinner,
    SectionHeader,
  },
  mixins: [stringUtils],
  data() {
    return {
      routeEventsRepository: null,
      loadingTemplates: true,
      loadingVehicles: true,
      loadingRouteEvents: true,
      routes: [],
      templates: [],
      vehicles: [],
    }
  },
  computed: {
    calendarButtonText() {
      // fullcalendar does a poor job of translation some UI elements
      return {
        prev: '',
        next: '',
        prevYear: this.$t('calendar.previousYear'),
        nextYear: this.$t('calendar.nextYear'),
        today: this.$t('calendar.today'),
        year: this.$t('calendar.year'),
        month: this.$t('calendar.month'),
        week: this.$t('calendar.week'),
        day: this.$t('calendar.day'),
        list: this.$t('calendar.list'),
      }
    },
    calendarOptions() {
      const self = this

      const locale = this.$i18n.locale
      const options = {
        locale,
        firstDay: 1,
        timeZone: 'Europe/Berlin',
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'currentWeek',
        events: async function (info, success) {
          const routeEvents = await self.routeEventsRepository.get(info.start, info.end)
          success(routeEvents)
        },
        loading: function (isLoading) {
          self.loadingRouteEvents = isLoading
        },
        lazyFetching: false, // Couldn't get lazyFetching working so we're using our own caching solution
        eventClick: this.eventClick,
        drop: this.eventDrop,
        droppable: true,
        views: {
          currentWeek: {
            type: 'dayGridMonth',
            duration: { weeks: 6 },
          },
        },
      }
      return locale === 'de' ? { ...options, buttonText: this.calendarButtonText } : options
    },
  },
  methods: {
    async getTemplates() {
      const templatesResult = await routeTemplatesApi.getAllForCompany(this.companyUser.company_uuid)
      this.templates = templatesResult.filter(template => template.template_type !== 'static_wdl')
      this.loadingTemplates = false
    },
    async getVehicles() {
      this.vehicles = await vehiclesApi.getAllForCompany(this.companyUser.company_uuid)
      this.routeEventsRepository = new RouteEventsRepository(this.vehicles)
      this.loadingVehicles = false
    },
    eventClick(event_info) {
      this.$router.push(`/company/route_events/${event_info.event._def.extendedProps.uuid}`)
    },
    async eventDrop(dropInfo) {
      const selectedTemplate = this.templates.find(template => template.uuid == dropInfo.draggedEl.dataset.uuid)
      const vehicleUuids = selectedTemplate.vehicles.map(vehicle => vehicle.vehicle.uuid)
      const created = await routeEventsApi.create({
        event_date: dropInfo.date,
        company_uuid: this.companyUser.company_uuid,
        template_uuid: selectedTemplate.uuid,
        project_uuid: selectedTemplate.project_uuid,
        vehicle_uuids: vehicleUuids,
      })
      if (created) {
        await this.routeEventsRepository.refetch(dropInfo.date, dropInfo.date)

        const fullCalendar = this.$refs.fullCalendar.getApi()
        fullCalendar.refetchEvents()
      }
    },
    initDraggable() {
      var containerEl = document.getElementById('external-events')
      new Draggable(containerEl, {
        itemSelector: '.fc-event',
      })
    },
    templateTypeTitle(templateType) {
      return templateType ? this.$t(`routes.template${this.capitalizeCamelCase(templateType)}`) : '—'
    },
  },
  created() {
    this.getTemplates()
    this.getVehicles()
  },
  mounted() {
    this.initDraggable()
  },
}
</script>

<style lang="scss">
.route-template-wrapper {
  --gap: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: var(--gap);
  padding: 0;
  margin: 0;
  list-style-type: none;
}

.route-template {
  border: 1px solid #f1f1f1;
  background: #f9f9f9;
  padding: 0.825rem 0.5rem 0.825rem 0.825rem;
  border-radius: var(--border-radius-m);
  width: calc(20% - 10px);
  cursor: move;
  cursor: grab;
  cursor: -moz-grab;
  cursor: -webkit-grab;
  position: relative;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  align-items: flex-start;
  /* number of columns = 4 */
  width: calc((100% / 4) - var(--gap) + (var(--gap) / 4));

  .material-icons {
    font-size: 16px;
    padding: 4px;
  }

  .material-icons:hover {
    background: #269a6c;
    color: #fff;
    border-radius: 50%;
  }

  a {
    color: #444;
  }

  h3 {
    margin: 0 0 5px 0;
    font-weight: 600;
    font-size: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }

  p {
    margin: 0;
    color: #888;
  }
}

.route-template:hover {
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.05);
}

@media only screen and (max-width: 991px) {
  .route-template {
    /* number of columns = 3 */
    width: calc((100% / 3) - var(--gap) + (var(--gap) / 3));
  }
}

@media only screen and (max-width: 767px) {
  .route-template {
    /* number of columns = 2 */
    width: calc((100% / 2) - var(--gap) + (var(--gap) / 2));
  }
}

@media only screen and (max-width: 659px) {
  .route-template {
    /* number of columns = 1 */
    width: 100%;
  }
}

.fc .fc-toolbar {
  gap: 0.75rem;
  flex-wrap: wrap;
}

.fc .fc-toolbar-title {
  font-size: 1.375rem;
  font-weight: 400;
}

.fc-direction-ltr .fc-daygrid-event.fc-event-end,
.fc-direction-rtl .fc-daygrid-event.fc-event-start {
  border: 1px solid #f1f1f1;
  background: #f9f9f9;
  background-color: #f9f9f9;
  padding: 10px;
  border-radius: 3px;
}

.fc-h-event .fc-event-main {
  color: #444;
  font-size: 14px;
  font-weight: 600;
}
</style>
